import { championImageUrl } from '../config';
import { POSITIONS } from '../positions';
import type { ChampionSummary, PositionChoice, Recommendation, RecFor } from '../types';

interface Props {
  recFor: RecFor;
  onRecForChange: (r: RecFor) => void;
  position: PositionChoice;
  onPositionChange: (p: PositionChoice) => void;
  allRoles: string[];
  focusRoles: string[];
  onToggleFocusRole: (role: string) => void;
  onClearFocus: () => void;
  recommendations: Recommendation[];
  loading: boolean;
  error: string | null;
  /** Add the recommended champion to whichever team is being advised. */
  onPick: (c: ChampionSummary) => void;
}

/** The recommendation panel: advises either team, with your-role + focus filters. */
export default function Recommendations({
  recFor,
  onRecForChange,
  position,
  onPositionChange,
  allRoles,
  focusRoles,
  onToggleFocusRole,
  onClearFocus,
  recommendations,
  loading,
  error,
  onPick,
}: Props) {
  return (
    <section className="recs">
      <div className="recs-tabs" role="tablist" aria-label="Recommend for which team">
        <button
          type="button"
          role="tab"
          className={`recs-tab${recFor === 'ally' ? ' recs-tab--active' : ''}`}
          onClick={() => onRecForChange('ally')}
        >
          Your team
        </button>
        <button
          type="button"
          role="tab"
          className={`recs-tab recs-tab--enemy${recFor === 'enemy' ? ' recs-tab--active' : ''}`}
          onClick={() => onRecForChange('enemy')}
        >
          Enemy team
        </button>
      </div>

      <div className="recs-header">
        <h2>{recFor === 'ally' ? 'Best picks for you' : 'What the enemy should pick'}</h2>
        {loading && <span className="recs-status">updating…</span>}
      </div>

      {recFor === 'ally' && (
        <div className="focus-row">
          <span className="focus-label">Your role:</span>
          <button
            type="button"
            className={`chip chip--sm${position === 'any' ? ' chip--active' : ''}`}
            onClick={() => onPositionChange('any')}
          >
            Any
          </button>
          {POSITIONS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`chip chip--sm${position === p.key ? ' chip--active' : ''}`}
              onClick={() => onPositionChange(position === p.key ? 'any' : p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      <div className="focus-row">
        <span className="focus-label">Focus role:</span>
        {allRoles.map((r) => (
          <button
            key={r}
            type="button"
            className={`chip chip--sm${focusRoles.includes(r) ? ' chip--active' : ''}`}
            onClick={() => onToggleFocusRole(r)}
          >
            {r}
          </button>
        ))}
        {focusRoles.length > 0 && (
          <button type="button" className="focus-clear" onClick={onClearFocus}>
            clear
          </button>
        )}
      </div>

      {error && <p className="recs-error">{error}</p>}

      {!error && recommendations.length === 0 && !loading && (
        <p className="recs-empty">
          {focusRoles.length > 0
            ? 'No champions match that role focus for the current draft. Try clearing the focus.'
            : 'Add champions to either team to see recommended picks. Recommendations always show — in case it’s your turn.'}
        </p>
      )}

      <ol className="rec-list">
        {recommendations.map((rec, i) => {
          const url = championImageUrl(rec.champion.image);
          return (
            <li key={rec.champion.id} className="rec">
              <span className="rec-rank">{i + 1}</span>
              <button type="button" className="rec-icon" onClick={() => onPick(rec.champion)} title={`Add ${rec.champion.name} to the ${recFor === 'ally' ? 'your' : 'enemy'} team`}>
                {url && <img src={url} alt={rec.champion.name} width={48} height={48} />}
              </button>
              <div className="rec-body">
                <div className="rec-title">
                  <span className="rec-name">{rec.champion.name}</span>
                  <span className="rec-roles">{rec.champion.roles.join(' · ')}</span>
                </div>
                <div className="rec-score">
                  <div className="rec-score-bar" style={{ width: `${rec.score}%` }} />
                  <span className="rec-score-num">{Math.round(rec.score)}</span>
                </div>
                {rec.reasons.length > 0 && (
                  <ul className="rec-reasons">
                    {rec.reasons.map((r, j) => (
                      <li key={j}>{r}</li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
